import { requireCookie } from '@phi/cookies/use-cookie';
import { months } from '@phi/util';
import { type ComputedRef, type PropType, computed, defineComponent } from 'vue';
import { type IAnalytics } from '../use-cases';

export type ICookieConsentProvisions = {
    consent: ComputedRef<boolean>;
    shouldAsk: ComputedRef<boolean>;
    updateConsent: (consent: boolean) => void;
}

export const CONSENT_KEY = 'phi-consent';
export const CONSENT_TIMESTAMP_KEY = 'phi-consent-at';
const SIX_MONTHS_MS = months(6);

type Consent = 'grant' | 'deny';

export const CookieConsentProvider = defineComponent({
    props: {
        analytics: {
            required: true,
            type: Object as PropType<IAnalytics>,
        },
    },

    setup(props, { slots }) {
        const analytics = props.analytics;
        const cookie = requireCookie();
        const permission = computed(() => cookie.value[CONSENT_KEY] as undefined | Consent);
        const time = computed(() => parseFloat(cookie.value[CONSENT_TIMESTAMP_KEY] || '0'));
        const dt = computed(() => Date.now() - time.value);

        const consent = computed(() =>
            permission.value === 'grant' && dt.value < SIX_MONTHS_MS
        );
        const shouldAsk = computed(() => dt.value >= SIX_MONTHS_MS);

        function updateConsent(consent: boolean) {
            cookie.value = {
                ...cookie.value,
                [CONSENT_KEY]: consent ? 'grant' : 'deny',
                [CONSENT_TIMESTAMP_KEY]: Date.now().toString(),
            };
            analytics.consent(consent);
        }

        const provisions: ICookieConsentProvisions  = {
            consent,
            shouldAsk,
            updateConsent,
        };

        return () => slots.default ? slots.default(provisions) : '';
    },
});
